import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

import { API } from '@/config/api';

const HotelContext = createContext(null);

export const useHotel = () => {
  const context = useContext(HotelContext);
  if (!context) throw new Error('useHotel must be used within a HotelProvider');
  return context;
};

export const HotelProvider = ({ children }) => {
  const { user, getAuthHeaders } = useAuth();
  const [hotel, setHotel] = useState(null);
  const [myHotels, setMyHotels] = useState([]);
  const [activeHotelId, setActiveHotelId] = useState(null);
  const [hotelSettings, setHotelSettings] = useState({ visible_modules: ['dashboard', 'rooms', 'reservations', 'guests', 'billing', 'housekeeping', 'maintenance', 'transfers', 'reports', 'staff', 'settings', 'expenses'] });
  const [roomTypes, setRoomTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  // The effective hotel id: activeHotelId (switcher) or fallback to user.hotel_id
  const effectiveHotelId = activeHotelId || user?.hotel_id;

  // Fetch all hotels this user owns
  const fetchMyHotels = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/hotels`, { headers: getAuthHeaders() });
      const hotels = response.data || [];
      setMyHotels(hotels);
      
      // Get stored hotel ID from localStorage
      const storedHotelId = localStorage.getItem('active_hotel_id');
      
      // Check if user owns the stored hotel
      const ownsStoredHotel = storedHotelId && hotels.some(h => h.id === storedHotelId);
      
      if (ownsStoredHotel) {
        // User owns this hotel, use it
        setActiveHotelId(storedHotelId);
      } else if (hotels.length > 0) {
        // User doesn't own stored hotel or no stored hotel
        // Use the first hotel they own, or their primary hotel_id
        const defaultHotel = hotels.find(h => h.id === user.hotel_id) || hotels[0];
        setActiveHotelId(defaultHotel.id);
        localStorage.setItem('active_hotel_id', defaultHotel.id);
      } else if (user.hotel_id) {
        // Fallback to user's primary hotel_id
        setActiveHotelId(user.hotel_id);
        localStorage.setItem('active_hotel_id', user.hotel_id);
      } else {
        // No hotels at all - clear any stale data
        localStorage.removeItem('active_hotel_id');
        setActiveHotelId(null);
        setHotel(null);
        setRooms([]);
        setRoomTypes([]);
      }
    } catch (error) {
      console.error('Failed to fetch hotels list:', error);
    }
  }, [user, getAuthHeaders]);

  const fetchHotel = useCallback(async () => {
    if (!effectiveHotelId) return;
    try {
      const response = await axios.get(`${API}/hotels/${effectiveHotelId}`, { headers: getAuthHeaders() });
      setHotel(response.data);
    } catch (error) {
      console.error('Failed to fetch hotel:', error);
    }
  }, [effectiveHotelId, getAuthHeaders]);

  const fetchHotelSettings = useCallback(async () => {
    if (!effectiveHotelId) return;
    try {
      const response = await axios.get(`${API}/hotels/${effectiveHotelId}/settings`, { headers: getAuthHeaders() });
      setHotelSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch hotel settings:', error);
    }
  }, [effectiveHotelId, getAuthHeaders]);

  const updateHotelSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/hotels/${effectiveHotelId}/settings`, newSettings, { headers: getAuthHeaders() });
      setHotelSettings(newSettings);
      return true;
    } catch (error) {
      console.error('Failed to update hotel settings:', error);
      return false;
    }
  };

  const fetchRoomTypes = useCallback(async () => {
    if (!effectiveHotelId) return;
    try {
      const response = await axios.get(`${API}/hotels/${effectiveHotelId}/room-types`, { headers: getAuthHeaders() });
      setRoomTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch room types:', error);
    }
  }, [effectiveHotelId, getAuthHeaders]);

  const fetchRooms = useCallback(async () => {
    if (!effectiveHotelId) return;
    try {
      const response = await axios.get(`${API}/hotels/${effectiveHotelId}/rooms`, { headers: getAuthHeaders() });
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    }
  }, [effectiveHotelId, getAuthHeaders]);

  // Switch the active hotel and reload all data
  const switchHotel = useCallback((hotelId) => {
    localStorage.setItem('active_hotel_id', hotelId);
    setActiveHotelId(hotelId);
  }, []);

  // Delete a hotel owned by the user
  const deleteHotel = useCallback(async (hotelId) => {
    try {
      await axios.delete(`${API}/hotels/${hotelId}`, { headers: getAuthHeaders() });
      const remaining = myHotels.filter(h => h.id !== hotelId);
      setMyHotels(remaining);
      // If we deleted the active hotel, switch to the first remaining one
      if (hotelId === effectiveHotelId && remaining.length > 0) {
        switchHotel(remaining[0].id);
      }
      return true;
    } catch (error) {
      console.error('Failed to delete hotel:', error);
      return false;
    }
  }, [myHotels, effectiveHotelId, getAuthHeaders, switchHotel]);

  // Create a new hotel
  const createHotel = useCallback(async (hotelData) => {
    try {
      const response = await axios.post(`${API}/hotels`, hotelData, { headers: getAuthHeaders() });
      const newHotel = response.data;
      setMyHotels(prev => [...prev, newHotel]);
      switchHotel(newHotel.id);
      return { ok: true, hotel: newHotel };
    } catch (error) {
      console.error('Failed to create hotel:', error);
      return { ok: false, error: error?.response?.data?.detail || 'Failed to create hotel' };
    }
  }, [getAuthHeaders, switchHotel]);

  // Re-fetch all hotel data when effectiveHotelId changes
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchHotel(), fetchRoomTypes(), fetchRooms(), fetchHotelSettings()]);
      setLoading(false);
    };
    if (effectiveHotelId) {
      init();
    } else {
      setLoading(false);
    }
  }, [effectiveHotelId, fetchHotel, fetchRoomTypes, fetchRooms, fetchHotelSettings]);

  // Fetch hotels list on login
  useEffect(() => {
    if (user) fetchMyHotels();
  }, [user, fetchMyHotels]);

  const refreshData = async () => {
    await Promise.all([fetchHotel(), fetchRoomTypes(), fetchRooms(), fetchHotelSettings()]);
  };

  return (
    <HotelContext.Provider value={{ 
      hotel,
      myHotels,
      activeHotelId: effectiveHotelId,
      switchHotel,
      deleteHotel,
      createHotel,
      hotelSettings,
      roomTypes,
      rooms,
      loading,
      fetchHotel,
      fetchHotelSettings,
      updateHotelSettings,
      fetchRoomTypes,
      fetchRooms,
      refreshData,
      setRooms,
      setRoomTypes
    }}>
      {children}
    </HotelContext.Provider>
  );
};
